/**
 * Data-only builders for the signed-in home screen (urgent strip + weekly insights).
 *
 * Time saved estimate: we assume manually grading with written feedback would take
 * **6–12 minutes per scanned page** (one side). Range is intentionally conservative.
 */

import { AppPhase, type GradedWork } from '../types';

export type HomeScheduleItemLite = {
  id: string;
  title: string;
  date: string;
  kind?: 'event' | 'reminder' | 'teacherBlock' | 'appointment' | 'meeting' | 'holiday';
  time?: string;
};

export type HomeUrgentAlert = {
  id: string;
  tone: 'danger' | 'warning' | 'info';
  title: string;
  subtitle?: string;
  actionLabel: string;
  action:
    | { type: 'phase'; phase: AppPhase }
    | { type: 'schedule_item'; itemId: string; dateISO: string };
};

export type HomeWeekInsights = {
  sessionsGraded7d: number;
  pagesGraded7d: number;
  syncedEntries7d: number;
  minutesSavedLow: number;
  minutesSavedHigh: number;
  estimateBlurb: string;
};

/** Compact “top 3” actions for the signed-in home priority strip (blueprint: highest-impact next steps). */
export type HomeTopPriorityAction = {
  id: string;
  tone: 'danger' | 'warning' | 'emphasis' | 'neutral';
  /** i18n key under `home.priority.*` */
  messageKey: string;
  messageParams?: Record<string, number | string>;
  action:
    | { type: 'phase'; phase: AppPhase }
    | { type: 'schedule_item'; itemId: string; dateISO: string }
    | { type: 'google_connect' };
};

/** Upcoming rows for the signed-in home “events & reminders” section. */
export type HomeScheduleHighlight = {
  id: string;
  title: string;
  whenLabel: string;
  dateISO: string;
  kind?: HomeScheduleItemLite['kind'];
};

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatHighlightWhenLabel(when: Date, item: HomeScheduleItemLite, now: Date): string {
  if (item.time) {
    const timeStr = when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    if (sameCalendarDay(when, now)) return `Today · ${timeStr}`;
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    if (sameCalendarDay(when, tomorrow)) return `Tomorrow · ${timeStr}`;
    return `${when.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} · ${timeStr}`;
  }
  if (sameCalendarDay(when, now)) return 'Today (all day)';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  if (sameCalendarDay(when, tomorrow)) return 'Tomorrow (all day)';
  try {
    return when.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return 'Upcoming';
  }
}

/**
 * Next occurrences on the schedule within ~14 days (not yet ended), for the home hub.
 */
export function buildHomeScheduleHighlights(
  items: HomeScheduleItemLite[],
  now = new Date(),
  maxItems = 8
): HomeScheduleHighlight[] {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const horizonEnd = new Date(startOfToday);
  horizonEnd.setDate(horizonEnd.getDate() + 14);
  horizonEnd.setHours(23, 59, 59, 999);

  const rows: { when: Date; end: Date; item: HomeScheduleItemLite }[] = [];

  for (const item of items) {
    const when = parseItemWhen(item);
    if (!when) continue;
    const end = new Date(when);
    if (!item.time) {
      end.setHours(23, 59, 59, 999);
    }
    if (end.getTime() < now.getTime()) continue;
    const dayStart = new Date(when);
    dayStart.setHours(0, 0, 0, 0);
    if (dayStart.getTime() > horizonEnd.getTime()) continue;

    rows.push({ when, end, item });
  }

  rows.sort((a, b) => a.when.getTime() - b.when.getTime());

  return rows.slice(0, maxItems).map(({ when, item }) => ({
    id: item.id,
    title: item.title?.trim() || 'Scheduled item',
    dateISO: item.date,
    kind: item.kind,
    whenLabel: formatHighlightWhenLabel(when, item, now),
  }));
}

const MINUTES_PER_PAGE_LOW = 6;
const MINUTES_PER_PAGE_HIGH = 12;
const LARGE_PENDING_QUEUE = 12;
const REMINDER_KINDS = new Set<NonNullable<HomeScheduleItemLite['kind']>>([
  'event',
  'reminder',
  'appointment',
  'meeting',
]);

function parseItemWhen(item: HomeScheduleItemLite): Date | null {
  const parts = item.date.split('-').map((x) => Number(x));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, mo, d] = parts;
  const dt = new Date(y, mo - 1, d);
  if (item.time) {
    const [hRaw, mRaw] = item.time.split(':');
    const h = Number(hRaw);
    const m = Number(mRaw ?? '0');
    if (!Number.isNaN(h)) dt.setHours(h, Number.isNaN(m) ? 0 : m, 0, 0);
    else dt.setHours(0, 0, 0, 0);
  } else {
    dt.setHours(0, 0, 0, 0);
  }
  return dt;
}

/**
 * Reminders / meetings / events starting within the next 24h (or that started up to 30m ago).
 */
export function collectUpcomingReminderLines(
  items: HomeScheduleItemLite[],
  now = new Date()
): { lines: string[]; firstItem?: { id: string; dateISO: string } } {
  const windowEnd = now.getTime() + 24 * 60 * 60 * 1000;
  const windowStart = now.getTime() - 30 * 60 * 1000;
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  const rows: { when: number; title: string; id: string; dateISO: string; hasTime: boolean }[] = [];

  for (const item of items) {
    const k = item.kind ?? 'reminder';
    if (!REMINDER_KINDS.has(k)) continue;
    const when = parseItemWhen(item);
    if (!when) continue;
    const t = when.getTime();
    if (item.time) {
      if (t < windowStart || t > windowEnd) continue;
    } else {
      if (t < dayStart.getTime() || t >= dayEnd.getTime()) continue;
    }
    rows.push({
      when: t,
      title: item.title?.trim() || 'Scheduled item',
      id: item.id,
      dateISO: item.date,
      hasTime: !!item.time,
    });
  }

  rows.sort((a, b) => a.when - b.when);
  const lines = rows.slice(0, 4).map((r) => {
    const timeLabel = r.hasTime
      ? new Date(r.when).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : 'Today';
    return `${timeLabel} — ${r.title}`;
  });

  const first = rows[0];
  return {
    lines,
    firstItem: first ? { id: first.id, dateISO: first.dateISO } : undefined,
  };
}

export function buildHomeWeekInsights(history: GradedWork[], now = new Date()): HomeWeekInsights {
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const cutoff = now.getTime() - sevenDaysMs;
  const recent = history.filter((w) => w.timestamp >= cutoff);
  let pages = 0;
  for (const w of recent) {
    const n = w.imageUrls?.length || 1;
    pages += n;
  }
  const syncedEntries7d = recent.filter((w) => w.status === 'synced').length;
  const minutesSavedLow = pages * MINUTES_PER_PAGE_LOW;
  const minutesSavedHigh = pages * MINUTES_PER_PAGE_HIGH;
  return {
    sessionsGraded7d: recent.length,
    pagesGraded7d: pages,
    syncedEntries7d,
    minutesSavedLow,
    minutesSavedHigh,
    estimateBlurb: `We estimate ~${MINUTES_PER_PAGE_LOW}–${MINUTES_PER_PAGE_HIGH} min saved per scanned page vs handwritten rubric scoring and typing feedback.`,
  };
}

export function buildHomeUrgentAlerts(input: {
  syncStatus: 'idle' | 'ok' | 'error';
  pendingSyncCount: number;
  parentFollowUpsOpen: number;
  scheduleItems: HomeScheduleItemLite[];
  now?: Date;
}): HomeUrgentAlert[] {
  const now = input.now ?? new Date();
  const alerts: HomeUrgentAlert[] = [];

  if (input.syncStatus === 'error') {
    alerts.push({
      id: 'sync-error',
      tone: 'danger',
      title: 'Classroom data didn’t sync',
      subtitle: 'Check your connection and open Grade to retry.',
      actionLabel: 'Open Grade',
      action: { type: 'phase', phase: AppPhase.DASHBOARD },
    });
  }

  if (input.pendingSyncCount >= LARGE_PENDING_QUEUE) {
    alerts.push({
      id: 'large-queue',
      tone: 'warning',
      title: `${input.pendingSyncCount} submissions waiting to sync`,
      subtitle: 'Batch a sync when you have a minute so nothing piles up.',
      actionLabel: 'Review & sync',
      action: { type: 'phase', phase: AppPhase.DASHBOARD },
    });
  }

  if (input.parentFollowUpsOpen > 0) {
    alerts.push({
      id: 'parent-followups',
      tone: 'info',
      title: `${input.parentFollowUpsOpen} parent follow‑up${input.parentFollowUpsOpen === 1 ? '' : 's'}`,
      subtitle: 'Log or send messages when you’re ready.',
      actionLabel: 'Open Communicate',
      action: { type: 'phase', phase: AppPhase.RECORDS },
    });
  }

  const { lines, firstItem } = collectUpcomingReminderLines(input.scheduleItems, now);
  if (lines.length > 0) {
    alerts.push({
      id: 'schedule-reminders',
      tone: 'info',
      title: 'Coming up on your schedule',
      subtitle: lines.join(' · '),
      actionLabel: firstItem ? 'Jump to item' : 'Open schedule',
      action: firstItem
        ? { type: 'schedule_item', itemId: firstItem.id, dateISO: firstItem.dateISO }
        : { type: 'phase', phase: AppPhase.SCHEDULE },
    });
  }

  return alerts;
}

/**
 * Up to three highest-impact next actions for Home (deduped drivers; calm, scannable strip).
 */
export function buildHomeTopPriorityActions(input: {
  accessToken: string | null;
  syncStatus: 'idle' | 'ok' | 'error';
  pendingSyncCount: number;
  assignmentsToGrade: number;
  parentsToContact: number;
  lesson?: {
    title: string;
    scheduleItemId?: string;
    scheduleDate?: string;
  };
  max?: number;
}): HomeTopPriorityAction[] {
  const max = input.max ?? 3;
  const out: HomeTopPriorityAction[] = [];
  const seen = new Set<string>();

  const push = (a: HomeTopPriorityAction) => {
    if (out.length >= max || seen.has(a.id)) return;
    seen.add(a.id);
    out.push(a);
  };

  if (!input.accessToken?.trim()) {
    push({
      id: 'connect-google',
      tone: 'emphasis',
      messageKey: 'connectGoogle',
      action: { type: 'google_connect' },
    });
  }

  if (input.accessToken?.trim() && input.syncStatus === 'error') {
    push({
      id: 'sync-error',
      tone: 'danger',
      messageKey: 'syncError',
      action: { type: 'phase', phase: AppPhase.DASHBOARD },
    });
  }

  if (input.accessToken?.trim() && input.pendingSyncCount >= LARGE_PENDING_QUEUE) {
    push({
      id: 'large-queue',
      tone: 'warning',
      messageKey: 'largeQueue',
      messageParams: { count: input.pendingSyncCount },
      action: { type: 'phase', phase: AppPhase.DASHBOARD },
    });
  }

  if (input.accessToken?.trim() && input.assignmentsToGrade > 0) {
    push({
      id: 'grade-queue',
      tone: 'emphasis',
      messageKey: input.assignmentsToGrade === 1 ? 'gradeOne' : 'gradeMany',
      messageParams:
        input.assignmentsToGrade === 1 ? undefined : { count: input.assignmentsToGrade },
      action: { type: 'phase', phase: AppPhase.DASHBOARD },
    });
  }

  if (input.accessToken?.trim() && input.parentsToContact > 0) {
    push({
      id: 'parent-followups',
      tone: 'neutral',
      messageKey: input.parentsToContact === 1 ? 'parentsOne' : 'parentsMany',
      messageParams: input.parentsToContact === 1 ? undefined : { count: input.parentsToContact },
      action: { type: 'phase', phase: AppPhase.RECORDS },
    });
  }

  const lid = input.lesson?.scheduleItemId;
  const ldate = input.lesson?.scheduleDate;
  if (input.accessToken?.trim() && lid && ldate && input.lesson?.title) {
    push({
      id: 'today-lesson',
      tone: 'neutral',
      messageKey: 'todayLesson',
      messageParams: { title: input.lesson.title },
      action: { type: 'schedule_item', itemId: lid, dateISO: ldate },
    });
  }

  if (input.accessToken?.trim()) {
    push({
      id: 'fallback-plan',
      tone: 'neutral',
      messageKey: 'openPlan',
      action: { type: 'phase', phase: AppPhase.PLAN },
    });
  }

  if (input.accessToken?.trim()) {
    push({
      id: 'fallback-schedule',
      tone: 'neutral',
      messageKey: 'openSchedule',
      action: { type: 'phase', phase: AppPhase.SCHEDULE },
    });
  }

  return out.slice(0, max);
}
